import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, Users, Mail, Clock, Phone, ChevronLeft, 
  Check, X, Trash2, RefreshCw, LogOut, Gift, Music,
  BarChart3, Plus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [authHeader, setAuthHeader] = useState("");
  const [bookings, setBookings] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [events, setEvents] = useState([]);
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [newEvent, setNewEvent] = useState({ title: "", description: "", date: "", image_url: "" });
  const [newOffer, setNewOffer] = useState({ title: "", description: "", valid_until: "", discount: "" });

  const fetchData = useCallback(async () => {
    if (!authHeader) return;
    setLoading(true);
    try {
      const [bookingsRes, subscribersRes, statsRes, eventsRes, offersRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/newsletter`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/admin/stats`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/events`),
        axios.get(`${API}/offers`),
      ]);
      setBookings(bookingsRes.data);
      setSubscribers(subscribersRes.data);
      setStats(statsRes.data);
      setEvents(eventsRes.data);
      setOffers(offersRes.data);
    } catch (e) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const auth = "Basic " + btoa(`${credentials.username}:${credentials.password}`);
    try {
      await axios.get(`${API}/admin/stats`, { headers: { Authorization: auth } });
      setAuthHeader(auth);
      setIsAuthenticated(true);
      toast.success("Logged in successfully");
    } catch (e) {
      toast.error("Invalid credentials");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthHeader("");
    setCredentials({ username: "", password: "" });
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { status }, { headers: { Authorization: authHeader } });
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (e) {
      toast.error("Failed to update booking");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers: { Authorization: authHeader } });
      toast.success("Booking deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete booking");
    }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, newEvent, { headers: { Authorization: authHeader } });
      toast.success("Event created");
      setNewEvent({ title: "", description: "", date: "", image_url: "" });
      fetchData();
    } catch (e) {
      toast.error("Failed to create event");
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/events/${id}`, { headers: { Authorization: authHeader } });
      toast.success("Event deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete event");
    }
  };

  const createOffer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/offers`, newOffer, { headers: { Authorization: authHeader } });
      toast.success("Offer created");
      setNewOffer({ title: "", description: "", valid_until: "", discount: "" });
      fetchData();
    } catch (e) {
      toast.error("Failed to create offer");
    }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    try {
      await axios.delete(`${API}/offers/${id}`, { headers: { Authorization: authHeader } });
      toast.success("Offer deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete offer");
    }
  };

  const filteredBookings = bookings.filter(b => {
    if (filter === "all") return true;
    return b.status === filter;
  });

  const getStatusBadge = (status) => {
    const colors = {
      pending: "bg-yellow-500/20 text-yellow-500 border-yellow-500/30",
      confirmed: "bg-green-500/20 text-green-500 border-green-500/30",
      cancelled: "bg-red-500/20 text-red-500 border-red-500/30",
    };
    return colors[status] || colors.pending;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl text-gold mb-2">Liv's Lounge</h1>
            <p className="text-zinc-400">Admin Dashboard</p>
          </div>
          <form onSubmit={handleLogin} className="bg-zinc-900/50 border border-white/10 p-8 space-y-4">
            <Input
              placeholder="Username"
              value={credentials.username}
              onChange={(e) => setCredentials({...credentials, username: e.target.value})}
              required
              className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 rounded-none h-12"
              data-testid="admin-username-input"
            />
            <Input
              type="password"
              placeholder="Password"
              value={credentials.password}
              onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              required
              className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 rounded-none h-12"
              data-testid="admin-password-input"
            />
            <Button 
              type="submit"
              className="w-full bg-gold hover:bg-gold-500 text-black font-semibold rounded-none py-4 uppercase tracking-widest"
              data-testid="admin-login-btn"
            >
              Login
            </Button>
          </form>
          <div className="mt-4 text-center">
            <a href="/" className="text-zinc-500 hover:text-gold text-sm flex items-center justify-center gap-2">
              <ChevronLeft size={16} /> Back to website
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-zinc-900/50 border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-zinc-400 hover:text-white">
              <ChevronLeft size={20} />
            </a>
            <h1 className="font-serif text-xl text-gold">Admin Dashboard</h1>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="text-zinc-400 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="text-gold" size={20} />
                <span className="text-zinc-400 text-sm">Total Bookings</span>
              </div>
              <p className="text-3xl font-semibold text-white">{stats.total_bookings}</p>
            </div>
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="text-yellow-500" size={20} />
                <span className="text-zinc-400 text-sm">Pending</span>
              </div>
              <p className="text-3xl font-semibold text-white">{stats.pending_bookings}</p>
            </div>
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Check className="text-green-500" size={20} />
                <span className="text-zinc-400 text-sm">Confirmed</span>
              </div>
              <p className="text-3xl font-semibold text-white">{stats.confirmed_bookings}</p>
            </div>
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Mail className="text-gold" size={20} />
                <span className="text-zinc-400 text-sm">Subscribers</span>
              </div>
              <p className="text-3xl font-semibold text-white">{stats.newsletter_subscribers}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="bg-zinc-900/50 border border-white/10 p-1">
            <TabsTrigger value="bookings" className="data-[state=active]:bg-gold data-[state=active]:text-black" data-testid="tab-bookings">
              <Calendar className="mr-2" size={16} /> Bookings
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-gold data-[state=active]:text-black" data-testid="tab-events">
              <Music className="mr-2" size={16} /> Events
            </TabsTrigger>
            <TabsTrigger value="offers" className="data-[state=active]:bg-gold data-[state=active]:text-black" data-testid="tab-offers">
              <Gift className="mr-2" size={16} /> Offers
            </TabsTrigger>
            <TabsTrigger value="subscribers" className="data-[state=active]:bg-gold data-[state=active]:text-black" data-testid="tab-subscribers">
              <Mail className="mr-2" size={16} /> Subscribers
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" data-testid="bookings-tab-content">
            <div className="bg-zinc-900/50 border border-white/10">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-40 bg-zinc-800 border-white/10 text-white rounded-none" data-testid="booking-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="all" className="text-white">All Bookings</SelectItem>
                      <SelectItem value="pending" className="text-white">Pending</SelectItem>
                      <SelectItem value="confirmed" className="text-white">Confirmed</SelectItem>
                      <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-zinc-400 text-sm">{filteredBookings.length} bookings</span>
                </div>
                <Button 
                  onClick={fetchData} 
                  variant="ghost" 
                  className="text-zinc-400 hover:text-white"
                  disabled={loading}
                  data-testid="refresh-btn"
                >
                  <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                </Button>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-zinc-400">Guest</TableHead>
                      <TableHead className="text-zinc-400">Contact</TableHead>
                      <TableHead className="text-zinc-400">Date & Time</TableHead>
                      <TableHead className="text-zinc-400">Guests</TableHead>
                      <TableHead className="text-zinc-400">Status</TableHead>
                      <TableHead className="text-zinc-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings.map((booking) => (
                      <TableRow key={booking.id} className="border-white/10" data-testid={`booking-row-${booking.id}`}>
                        <TableCell>
                          <p className="text-white font-medium">{booking.name}</p>
                          {booking.message && (
                            <p className="text-zinc-500 text-sm truncate max-w-[200px]">{booking.message}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-zinc-300 text-sm flex items-center gap-1">
                              <Phone size={12} /> {booking.phone}
                            </p>
                            <p className="text-zinc-400 text-sm flex items-center gap-1">
                              <Mail size={12} /> {booking.email}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-white">{booking.date}</p>
                          <p className="text-zinc-400 text-sm">{booking.time}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-zinc-300">
                            <Users size={14} /> {booking.guests}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusBadge(booking.status)} rounded-none`}>
                            {booking.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {booking.status === "pending" && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => updateBookingStatus(booking.id, "confirmed")}
                                  className="bg-green-500/20 text-green-500 hover:bg-green-500/30 rounded-none h-8"
                                  data-testid={`confirm-booking-${booking.id}`}
                                >
                                  <Check size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => updateBookingStatus(booking.id, "cancelled")}
                                  className="bg-red-500/20 text-red-500 hover:bg-red-500/30 rounded-none h-8"
                                  data-testid={`cancel-booking-${booking.id}`}
                                >
                                  <X size={14} />
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              onClick={() => deleteBooking(booking.id)}
                              variant="ghost"
                              className="text-zinc-500 hover:text-red-500 h-8"
                              data-testid={`delete-booking-${booking.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredBookings.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                          No bookings found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" data-testid="events-tab-content">
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white font-semibold">Manage Events</h3>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-gold hover:bg-gold-500 text-black rounded-none" data-testid="add-event-btn">
                      <Plus size={16} className="mr-2" /> Add Event
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-white/10">
                    <DialogHeader>
                      <DialogTitle className="text-white">Create Event</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={createEvent} className="space-y-4 mt-4">
                      <Input
                        placeholder="Event Title"
                        value={newEvent.title}
                        onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                        required
                        className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 rounded-none"
                        data-testid="event-title-input"
                      />
                      <Input
                        placeholder="Date (e.g., Friday 20th December)"
                        value={newEvent.date}
                        onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                        required
                        className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 rounded-none"
                        data-testid="event-date-input"
                      />
                      <Textarea
                        placeholder="Description"
                        value={newEvent.description}
                        onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                        required
                        className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 rounded-none"
                        data-testid="event-description-input"
                      />
                      <Input
                        placeholder="Image URL (optional)"
                        value={newEvent.image_url}
                        onChange={(e) => setNewEvent({...newEvent, image_url: e.target.value})}
                        className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 rounded-none"
                        data-testid="event-image-input"
                      />
                      <Button type="submit" className="w-full bg-gold hover:bg-gold-500 text-black rounded-none" data-testid="create-event-btn">
                        Create Event
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {events.map((event) => (
                  <div key={event.id} className="flex items-center justify-between p-4 bg-zinc-800/50 border border-white/5" data-testid={`event-item-${event.id}`}>
                    <div>
                      <p className="text-gold text-sm">{event.date}</p>
                      <h4 className="text-white font-medium">{event.title}</h4>
                      <p className="text-zinc-400 text-sm">{event.description}</p>
                    </div>
                    <Button
                      onClick={() => deleteEvent(event.id)}
                      variant="ghost"
                      className="text-zinc-500 hover:text-red-500"
                      data-testid={`delete-event-${event.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
                {events.length === 0 && (
                  <p className="text-zinc-500 text-center py-8">No events created yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers" data-testid="offers-tab-content">
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white font-semibold">Manage Offers</h3>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-gold hover:bg-gold-500 text-black rounded-none" data-testid="add-offer-btn">
                      <Plus size={16} className="mr-2" /> Add Offer
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-white/10">
                    <DialogHeader>
                      <DialogTitle className="text-white">Create Special Offer</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={createOffer} className="space-y-4 mt-4">
                      <Input
                        placeholder="Offer Title"
                        value={newOffer.title}
                        onChange={(e) => setNewOffer({...newOffer, title: e.target.value})}
                        required
                        className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 rounded-none"
                        data-testid="offer-title-input"
                      />
                      <Textarea
                        placeholder="Description"
                        value={newOffer.description}
                        onChange={(e) => setNewOffer({...newOffer, description: e.target.value})}
                        required
                        className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 rounded-none"
                        data-testid="offer-description-input"
                      />
                      <Input
                        placeholder="Valid Until (e.g., 31st December 2024)"
                        value={newOffer.valid_until}
                        onChange={(e) => setNewOffer({...newOffer, valid_until: e.target.value})}
                        required
                        className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 rounded-none"
                        data-testid="offer-valid-input"
                      />
                      <Input
                        placeholder="Discount (e.g., 20% off)"
                        value={newOffer.discount}
                        onChange={(e) => setNewOffer({...newOffer, discount: e.target.value})}
                        className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 rounded-none"
                        data-testid="offer-discount-input"
                      />
                      <Button type="submit" className="w-full bg-gold hover:bg-gold-500 text-black rounded-none" data-testid="create-offer-btn">
                        Create Offer
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {offers.map((offer) => (
                  <div key={offer.id} className="flex items-center justify-between p-4 bg-zinc-800/50 border border-white/5" data-testid={`offer-item-${offer.id}`}>
                    <div>
                      <h4 className="text-white font-medium">{offer.title}</h4>
                      <p className="text-zinc-400 text-sm">{offer.description}</p>
                      <div className="flex items-center gap-4 mt-1">
                        {offer.discount && <span className="text-gold text-sm">{offer.discount}</span>}
                        <span className="text-zinc-500 text-sm">Valid until {offer.valid_until}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => deleteOffer(offer.id)}
                      variant="ghost"
                      className="text-zinc-500 hover:text-red-500"
                      data-testid={`delete-offer-${offer.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
                {offers.length === 0 && (
                  <p className="text-zinc-500 text-center py-8">No offers created yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers" data-testid="subscribers-tab-content">
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <h3 className="text-white font-semibold mb-6">Newsletter Subscribers ({subscribers.length})</h3>
              <div className="space-y-2">
                {subscribers.map((sub) => (
                  <div key={sub.id} className="flex items-center justify-between p-3 bg-zinc-800/50 border border-white/5" data-testid={`subscriber-${sub.id}`}>
                    <div className="flex items-center gap-3">
                      <Mail size={16} className="text-gold" />
                      <span className="text-white">{sub.email}</span>
                    </div>
                    <span className="text-zinc-500 text-sm">
                      {new Date(sub.subscribed_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
                {subscribers.length === 0 && (
                  <p className="text-zinc-500 text-center py-8">No subscribers yet</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
